import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import P2PMarketplaceService from '../../services/p2pMarketplace.service';
import { setFilters } from '../../request/P2P/p2pSlice';
// import LoadCurrencies from '../../request/Countries/LoadCurrencies';
import { 
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  useMediaQuery,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Skeleton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  
} from '@mui/material';
import {
  Search,
  TrendingUp,
  TrendingDown,
  Star,
  StarBorder,
  FilterList,
  History,
  Verified,
  LocationOn,
  Payment,
  AccessTime,
  AccountBalanceWallet,
  ArrowForward,
  ExpandMore,
  CheckCircle,
  Warning,
  Info,
  Chat,
  Phone,
  Email,
  AccountBalance,
  Security,
  Speed,
  SupportAgent,
  Close,
  Refresh,
  Timer,
  AttachMoney,
  TrendingFlat
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const P2PBuyInterface = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  // Redux state
  const fundingBal = useSelector((state) => state.wallet.fundingBalance);
  const coinsData = useSelector((state) => state.coins.coinRates) || [];
  const p2pOffers = useSelector((state) => state.p2p?.offers || []);
  const p2pLoading = useSelector((state) => state.p2p?.loading || false);
  const filters = useSelector((state) => state.p2p?.filters || {});
  
  // Get user data from unified system
  const userData = useSelector((state) => state.userData?.user);
  const userLoading = useSelector((state) => state.userData?.loading || false);
  
  // Load currencies from Redux store
  const currencies = useSelector((state) => state.countries.currencies) || [];

  // State
  const [selectedCrypto, setSelectedCrypto] = useState(filters.cryptocurrency || 'USDT');
  const [selectedFiat, setSelectedFiat] = useState(filters.fiatCurrency || 'NGN');
  const [searchAmount, setSearchAmount] = useState(filters.amount || '');
  const [paymentMethod, setPaymentMethod] = useState(filters.paymentMethod || '');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderStep, setOrderStep] = useState(0);
  const [orderAmount, setOrderAmount] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Use data from Redux store
  const offers = p2pOffers.filter(offer => 
    offer.type === 'sell' && // For buy interface, show sell offers
    offer.cryptocurrency === selectedCrypto &&
    offer.fiat_currency === selectedFiat &&
    (!paymentMethod || offer.payment_methods.includes(paymentMethod)) &&
    (!searchAmount || (offer.min_amount <= parseFloat(searchAmount) && offer.max_amount >= parseFloat(searchAmount)))
  );
  const loading = p2pLoading;

  // Update Redux filters when local state changes
  useEffect(() => {
    dispatch(setFilters({
      // For BUY, we want to see SELL offers, but API needs type=buy
      // because type refers to what the user wants to do
      tradeType: 'buy',
      cryptocurrency: selectedCrypto,
      fiatCurrency: selectedFiat,
      paymentMethod: paymentMethod || undefined,
      amount: searchAmount || undefined,
    }));
  }, [selectedCrypto, selectedFiat, searchAmount, paymentMethod, dispatch]);

  const handleCreateOrder = async (offer) => {
    // For buy orders, users don't need crypto balance since they're paying fiat to receive crypto
    setSelectedOffer(offer);
    setOrderAmount(searchAmount || offer.min_amount.toString());
    setShowOrderDialog(true);
    setOrderStep(0);
  };

  const handleConfirmOrder = async () => {
    if (!selectedOffer || !orderAmount || !agreeToTerms) {
      alert('Please fill in all required fields and agree to terms.');
      return;
    }

    setCreating(true);
    try {
      const orderData = {
        rate_id: selectedOffer.id,
        amount: parseFloat(orderAmount),
        payment_method: selectedOffer.payment_methods[0] || 'Bank Transfer',
        type: 'buy',
        notes: orderNotes
      };

      const response = await P2PMarketplaceService.createOrder(orderData);
      
      if (response.success) {
        setShowOrderDialog(false);
        navigate(`/p2p/transaction/${response.order.id}`);
      } else {
        alert(response.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to create order. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const formatPrice = (amount, currency = 'NGN') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatCryptoAmount = (amount, crypto) => {
    return `${parseFloat(amount).toFixed(8)} ${crypto}`;
  };

  const calculateReceiveAmount = (amount, rate) => {
    if (!amount || !rate) return '0.00000000';
    return (parseFloat(amount) / parseFloat(rate)).toFixed(8);
  };

  const renderOfferCard = (offer, index) => (
    <motion.div
      key={offer.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card 
        sx={{ 
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 8,
            transform: 'translateY(-4px)',
            borderColor: theme.palette.primary.main
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Partner Info */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    offer.partner.is_verified ? (
                      <Verified sx={{ fontSize: 16, color: 'primary.main' }} />
                    ) : null
                  }
                >
                  <Avatar
                    sx={{ 
                      width: 56, 
                      height: 56,
                      bgcolor: theme.palette.primary.main,
                      fontSize: '1.4rem',
                      fontWeight: 600
                    }}
                  >
                    {offer.partner.name.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {offer.partner.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i}
                          sx={{ 
                            fontSize: 16,
                            color: i < Math.floor(offer.partner.rating) ? 'warning.main' : 'grey.300'
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      ({offer.partner.rating.toFixed(1)})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {offer.partner.country || 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Rate & Amount */}
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.success.main,
                    mb: 1
                  }}
                >
                  {formatPrice(offer.rate, offer.fiat_currency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  per {offer.cryptocurrency}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Chip 
                    label={`${offer.partner.total_trades} trades`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${offer.partner.completion_rate}% completion`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Limits & Payment */}
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Available: {formatCryptoAmount(offer.available_balance, offer.cryptocurrency)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Limits: {formatPrice(offer.min_amount, offer.fiat_currency)} - {formatPrice(offer.max_amount, offer.fiat_currency)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Payment sx={{ fontSize: 14, color: 'text.secondary' }} />
                  {offer.payment_methods.slice(0, 2).map((method, idx) => (
                    <Chip 
                      key={idx}
                      label={method}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                  ))}
                  {offer.payment_methods.length > 2 && (
                    <Typography variant="caption" color="text.secondary">
                      +{offer.payment_methods.length - 2} more
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {offer.time_limit} min
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Action Button */}
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={() => handleCreateOrder(offer)}
                  disabled={creating || offer.available_balance <= 0}
                  endIcon={<ArrowForward />}
                  sx={{ 
                    minWidth: 160,
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2
                  }}
                >
                  Buy {offer.cryptocurrency}
                </Button>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                  <Chat sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Chat available
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );

  const steps = [
    'Select Amount',
    'Payment Details',
    'Confirm Order'
  ];

  return (
    <Box sx={{ p: 3 }}>

      {/* Filters */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Buy {selectedCrypto} with {selectedFiat}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                  <CircularProgress size={18} thickness={5} />
                  <Typography variant="body2" color="text.secondary">Loading</Typography>
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Amount Input */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={searchAmount}
                onChange={(e) => setSearchAmount(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBalanceWallet />
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter amount"
              />
            </Grid>

            {/* Fiat Currency */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={selectedFiat}
                  onChange={(e) => setSelectedFiat(e.target.value)}
                  label="Currency"
                >
                  {currencies.length > 0 ? (
                    currencies.map((currency) => (
                      <MenuItem key={currency.currency_code} value={currency.currency_code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {currency.flag && (
                            <img 
                              src={currency.flag} 
                              alt={currency.country_name}
                              style={{ width: 20, height: 15, objectFit: 'cover', borderRadius: '2px' }}
                            />
                          )}
                          <Typography variant="body2">
                            {currency.currency_code} ({currency.currency})
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    ['NGN', 'USD', 'EUR', 'GBP', 'CAD'].map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        <Typography variant="body2">{currency}</Typography>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Crypto Selection */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Crypto</InputLabel>
                <Select
                  value={selectedCrypto}
                  onChange={(e) => setSelectedCrypto(e.target.value)}
                  label="Crypto"
                >
                  {coinsData && coinsData.length > 0 ? (
                    coinsData.slice(0, 20).map((coin) => (
                      <MenuItem key={coin.symbol} value={coin.symbol}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={`https://assets.coincap.io/assets/icons/${coin.symbol.toLowerCase()}@2x.png`}
                            alt={coin.name}
                            style={{ width: 20, height: 20, objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Typography variant="body2">
                            {coin.symbol} - {coin.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    ['USDT', 'BTC', 'ETH', 'BNB', 'SOL'].map((crypto) => (
                      <MenuItem key={crypto} value={crypto}>
                        <Typography variant="body2">{crypto}</Typography>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Payment</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  label="Payment"
                >
                  <MenuItem value="">All Methods</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Mobile Money">Mobile Money</MenuItem>
                  <MenuItem value="PayPal">PayPal</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons removed: auto-load on selection; keep History only */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="outlined" startIcon={<History />} onClick={() => navigate('/p2p/history')}>
                  History
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          <Collapse in={showFilters}>
            <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Min Rating</InputLabel>
                    <Select value="" label="Min Rating">
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="4">4+ Stars</MenuItem>
                      <MenuItem value="3">3+ Stars</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Completion Rate</InputLabel>
                    <Select value="" label="Completion Rate">
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="95">95%+</MenuItem>
                      <MenuItem value="90">90%+</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Response Time</InputLabel>
                    <Select value="" label="Response Time">
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="15">15 min</MenuItem>
                      <MenuItem value="30">30 min</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Switch />}
                    label="Verified Only"
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Results */}
      <Box>
        {loading ? (
          <Box>
            {[...Array(3)].map((_, index) => (
              <Card key={index} sx={{ mb: 2, p: 3 }}>
                <Skeleton variant="rectangular" height={120} />
              </Card>
            ))}
          </Box>
        ) : offers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No offers available for the selected criteria
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or check back later
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {offers.length} Sell Offers Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sorted by lowest price
              </Typography>
            </Box>
            
            <AnimatePresence>
              {offers.map((offer, index) => renderOfferCard(offer, index))}
            </AnimatePresence>
          </Box>
        )}
      </Box>

      {/* Order Creation Dialog */}
      <Dialog 
        open={showOrderDialog} 
        onClose={() => setShowOrderDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Create Buy Order
            </Typography>
            <IconButton onClick={() => setShowOrderDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={orderStep} orientation={isMobile ? 'vertical' : 'horizontal'}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                  {index === 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Enter Amount
                      </Typography>
                      <TextField
                        fullWidth
                        label="Amount to Pay"
                        type="number"
                        value={orderAmount}
                        onChange={(e) => setOrderAmount(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {selectedFiat}
                            </InputAdornment>
                          ),
                          inputProps: {
                            min: selectedOffer?.min_amount || 0,
                            max: selectedOffer?.max_amount || 999999,
                          }
                        }}
                        error={orderAmount && (
                          parseFloat(orderAmount) < (selectedOffer?.min_amount || 0) ||
                          parseFloat(orderAmount) > (selectedOffer?.max_amount || 999999)
                        )}
                        helperText={
                          orderAmount && (
                            parseFloat(orderAmount) < (selectedOffer?.min_amount || 0) ||
                            parseFloat(orderAmount) > (selectedOffer?.max_amount || 999999)
                          ) ? (
                            `Amount must be between ${formatPrice(selectedOffer?.min_amount || 0, selectedOffer?.fiat_currency)} and ${formatPrice(selectedOffer?.max_amount || 999999, selectedOffer?.fiat_currency)}`
                          ) : (
                            `Limits: ${formatPrice(selectedOffer?.min_amount || 0, selectedOffer?.fiat_currency)} - ${formatPrice(selectedOffer?.max_amount || 999999, selectedOffer?.fiat_currency)}`
                          )
                        }
                        sx={{ mb: 2 }}
                      />
                      <Alert severity="info" sx={{ mb: 2 }}>
                        You will receive: {calculateReceiveAmount(orderAmount, selectedOffer?.rate)} {selectedOffer?.cryptocurrency}
                      </Alert>
                      <Button
                        variant="contained"
                        onClick={() => setOrderStep(1)}
                        disabled={
                          !orderAmount || 
                          parseFloat(orderAmount) < (selectedOffer?.min_amount || 0) ||
                          parseFloat(orderAmount) > (selectedOffer?.max_amount || 999999)
                        }
                      >
                        Continue
                      </Button>
                    </Box>
                  )}
                  
                  {index === 1 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Payment Details
                      </Typography>
                      <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Partner: {selectedOffer?.partner.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Rate: {formatPrice(selectedOffer?.rate, selectedOffer?.fiat_currency)} per {selectedOffer?.cryptocurrency}
                        </Typography>
                        
                        {/* Payment Methods and Details */}
                        {selectedOffer?.payment_methods && selectedOffer.payment_methods.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                              Available Payment Methods:
                            </Typography>
                            {selectedOffer.payment_methods.map((method, methodIndex) => (
                              <Card key={method} sx={{ mb: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                    {method}
                                  </Typography>
                                  {selectedOffer.payment_details && selectedOffer.payment_details[method] && (
                                    <Box sx={{ mt: 1 }}>
                                      {Object.entries(selectedOffer.payment_details[method]).map(([key, value]) => (
                                        value && (
                                          <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {value}
                                          </Typography>
                                        )
                                      ))}
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </Card>
                      <TextField
                        fullWidth
                        label="Notes (Optional)"
                        multiline
                        rows={3}
                        value={orderNotes}
                        onChange={(e) => setOrderNotes(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setOrderStep(0)}>
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => setOrderStep(2)}
                        >
                          Continue
                        </Button>
                      </Box>
                    </Box>
                  )}
                  
                  {index === 2 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Confirm Order
                      </Typography>
                      <Card sx={{ p: 2, mb: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Amount to Pay
                            </Typography>
                            <Typography variant="h6">
                              {formatPrice(orderAmount, selectedOffer?.fiat_currency)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              You Will Receive
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {calculateReceiveAmount(orderAmount, selectedOffer?.rate)} {selectedOffer?.cryptocurrency}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Card>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={agreeToTerms}
                            onChange={(e) => setAgreeToTerms(e.target.checked)}
                          />
                        }
                        label="I agree to the terms and conditions"
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setOrderStep(1)}>
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleConfirmOrder}
                          disabled={!agreeToTerms || creating}
                          startIcon={creating ? <CircularProgress size={20} /> : <CheckCircle />}
                        >
                          {creating ? 'Creating Order...' : 'Confirm Order'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default P2PBuyInterface;
